import {createGuid, GUID} from "../guids";
import {HasCode, LimitType, Severity} from "./types";

export type ClientCode = string

export type SaveState = 'Saved' | 'Dirty'

export interface ClientAlert {
  readonly id: GUID
  readonly thresholdInPercent: number
  readonly limitType: LimitType
  readonly contacts: ReadonlySet<Contact>
}

export interface Contact {
  readonly id: GUID
  readonly emailAddress: string
}

export interface VirtualServer {
  readonly id: GUID
  readonly serverId: string
}

export interface VirtualServerInput {
  readonly id: GUID
  readonly serverId: string
}

export interface ClientAlertForm {
  readonly id: GUID
  readonly thresholdInPercent: string
  readonly limitType: LimitType
  readonly contacts: ReadonlySet<ContactInput>
  readonly index: number
}

export interface ContactInput {
  readonly id: GUID
  readonly emailAddress: string
}

export interface ClientConfig extends HasCode {
  readonly alerts: ReadonlyArray<ClientAlert>
  readonly exposureLimitInJpy: number
  readonly id: GUID
  readonly originalId: GUID | undefined
  readonly name: string
  readonly isActive: boolean
  readonly tcsLimitInJPY: number
  readonly unactionedOrdersLimit: number
  readonly virtualServerGroupId: string
  readonly virtualServers: ReadonlySet<VirtualServer>
  readonly cumulativeOrderValueLimitInJPY: number
  readonly cumulativeOrderValueTimeIntervalSeconds: number
  readonly cumulativeExecutionValueLimitInJPY?: number
  readonly cumulativeExecutionValueTimeIntervalSeconds?: number
  readonly realisticExposureLimit: number
}

export interface ManageContactsForm {
  readonly associatedAlertForm: ClientAlertForm // Index and Id should remain the same on this
  readonly severity: Severity
}

export interface ClientForm extends HasCode {
  readonly awaitingSaveResult: boolean
  readonly alerts: ReadonlyArray<ClientAlertForm>
  readonly code: string
  readonly exposureLimitInJpy: string
  readonly id: GUID
  readonly originalId: GUID | undefined
  readonly name: string
  readonly isActive: boolean
  readonly originalClientCode: string | undefined
  readonly readyForConfirmation: boolean
  readonly saveState: SaveState
  readonly tcsLimitInJPY: string
  readonly unactionedOrdersLimit: string
  readonly realisticExposureLimit: string
  readonly virtualServerGroupId: string
  readonly manageContactsForm: ManageContactsForm | undefined
  readonly virtualServers: ReadonlySet<VirtualServerInput>
  readonly cumulativeOrderValueLimitInJPY: string
  readonly cumulativeOrderValueTimeIntervalSeconds: string
  readonly cumulativeExecutionValueLimitInJPY: string
  readonly cumulativeExecutionValueTimeIntervalSeconds: string
}

export function generateFormFromClient(client: ClientConfig): ClientForm {
  const alertsWithIndices = client.alerts.map(value => {
    return {...value, index: -value.thresholdInPercent}
  })
  return {
    alerts: alertsWithIndices.map((alert) => {
      return {
        contacts: new Set(
          [...alert.contacts].map((contact: ContactInput) => {
            return { id: createGuid(), emailAddress: contact.emailAddress }
          }),
        ),
        id: createGuid(),
        index: alert.index,
        limitType: alert.limitType,
        thresholdInPercent: alert.thresholdInPercent.toString(),
      }
    }),
    awaitingSaveResult: false,
    code: client.code,
    exposureLimitInJpy: client.exposureLimitInJpy.toString(),
    id: createGuid(),
    originalId: client.originalId,
    name: client.name,
    isActive: client.isActive,
    originalClientCode: client.code,
    readyForConfirmation: false,
    saveState: 'Saved',
    tcsLimitInJPY: client.tcsLimitInJPY.toString(),
    unactionedOrdersLimit: client.unactionedOrdersLimit.toString(),
    realisticExposureLimit: client.realisticExposureLimit.toString(),
    virtualServerGroupId: client.virtualServerGroupId,
    virtualServers: new Set(
      [...client.virtualServers].map((value: VirtualServerInput) => {
        return { serverId: value.serverId, id: createGuid() }
      }),
    ),
    manageContactsForm: undefined,
    cumulativeOrderValueLimitInJPY: client.cumulativeOrderValueLimitInJPY.toString(),
    cumulativeOrderValueTimeIntervalSeconds: client.cumulativeOrderValueTimeIntervalSeconds.toString(),
    cumulativeExecutionValueLimitInJPY: client.cumulativeExecutionValueLimitInJPY?.toString() || '',
    cumulativeExecutionValueTimeIntervalSeconds: client.cumulativeExecutionValueTimeIntervalSeconds?.toString() || '',
  }
}

export function createBlankClientForm(): ClientForm {
  const newClient: ClientForm = {
    alerts: [
      {
        contacts: new Set(),
        id: createGuid(),
        index: 0,
        limitType: 'RC1_total_clip_size',
        thresholdInPercent: '100',
      },
      {
        contacts: new Set(),
        id: createGuid(),
        index: 0,
        limitType: 'RC2_exposure',
        thresholdInPercent: '100',
      },
      {
        contacts: new Set(),
        id: createGuid(),
        index: 1,
        limitType: 'RC2_exposure',
        thresholdInPercent: '90',
      },
      {
        contacts: new Set(),
        id: createGuid(),
        index: 2,
        limitType: 'RC2_exposure',
        thresholdInPercent: '70',
      },
      {
        contacts: new Set(),
        id: createGuid(),
        index: 0,
        limitType: 'Realistic_exposure_value',
        thresholdInPercent: '100',
      },
      {
        contacts: new Set(),
        id: createGuid(),
        index: 0,
        limitType: 'RC6_unactioned_orders',
        thresholdInPercent: '100',
      },
      {
        contacts: new Set(),
        id: createGuid(),
        index: 1,
        limitType: 'RC6_unactioned_orders',
        thresholdInPercent: '90',
      },
      {
        contacts: new Set(),
        id: createGuid(),
        index: 2,
        limitType: 'RC6_unactioned_orders',
        thresholdInPercent: '70',
      },
      {
        contacts: new Set(),
        id: createGuid(),
        index: 3,
        limitType: 'Virtual_server_suspension',
        thresholdInPercent: '100',
      },
    ],
    manageContactsForm: undefined,
    awaitingSaveResult: false,
    code: '',
    isActive: false,
    exposureLimitInJpy: '',
    id: createGuid(),
    originalId: undefined,
    name: '',
    originalClientCode: undefined,
    readyForConfirmation: false,
    saveState: 'Dirty',
    tcsLimitInJPY: '',
    unactionedOrdersLimit: '',
    realisticExposureLimit: '0',
    virtualServerGroupId: '',
    virtualServers: new Set(),
    cumulativeOrderValueLimitInJPY: '',
    cumulativeOrderValueTimeIntervalSeconds: '',
    cumulativeExecutionValueLimitInJPY: '',
    cumulativeExecutionValueTimeIntervalSeconds: '',
  }
  return newClient
}
